import { useState, useEffect } from "react";
import { HardDrive, FolderOpen, FileText, Globe, Plus, X, Tag } from "lucide-react";
import { useAzure } from "@/context/AzureContext";
import { api } from "@/lib/api";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const REGIONS = ["eastus", "eastus2", "centralus", "westus2", "westus3", "northeurope", "westeurope", "uksouth"];
const SKUS = [
  ["Standard_LRS", "Locally-redundant storage (LRS)"],
  ["Standard_ZRS", "Zone-redundant storage (ZRS)"],
  ["Standard_GRS", "Geo-redundant storage (GRS)"],
  ["Standard_RAGRS", "Read-access geo-redundant (RA-GRS)"],
];

export default function StorageAccounts() {
  const { state, run, refresh } = useAzure();
  const accts = (state?.resources || []).filter((r) => r.type === "Microsoft.Storage/storageAccounts");
  const rgs = state?.resource_groups || [];
  const [selId, setSelId] = useState(null);
  const sel = accts.find((a) => a.id === selId) || null;
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", resource_group: "", location: "eastus", sku: "Standard_LRS" });
  const [tagRows, setTagRows] = useState([{ key: "", value: "" }]);
  const [cname, setCname] = useState("");
  const [bname, setBname] = useState("");
  const [bcont, setBcont] = useState("");
  const [sasPerms, setSasPerms] = useState("rl");
  const [sasUrl, setSasUrl] = useState("");
  const [detTagRows, setDetTagRows] = useState([{ key: "", value: "" }]);

  useEffect(() => {
    const t = sel?.tags || {};
    const rows = Object.entries(t).map(([key, value]) => ({ key, value }));
    setDetTagRows(rows.length ? rows : [{ key: "", value: "" }]);
  }, [selId]); // eslint-disable-line react-hooks/exhaustive-deps
  const saveDetailTags = () => {
    const tags = {};
    detTagRows.forEach(({ key, value }) => { const k = (key || "").trim(); if (k) tags[k] = value; });
    return act("/azure/storage/tags", { tags }, "Tags applied");
  };

  const act = async (url, payload, msg) => {
    const r = await run(() => api.post(url, { sa_id: sel.id, ...payload }), msg ? { success: msg } : {});
    if (r.ok) refresh();
    return r;
  };
  const addContainer = async () => { if (!cname) return; const r = await act("/azure/storage/containers", { name: cname }, "Container created"); if (r.ok) setCname(""); };
  const uploadBlob = async () => { if (!bname || !bcont) return; const r = await act("/azure/storage/blobs/upload", { container: bcont, name: bname, size_kb: 512, tier: "Hot" }, "Blob uploaded"); if (r.ok) setBname(""); };
  const cycleTier = (c, b) => { const order = ["Hot", "Cool", "Cold", "Archive"]; const next = order[(order.indexOf(b.tier) + 1) % order.length]; act("/azure/storage/blobs/tier", { container: c.name, name: b.name, tier: next }, `Tier → ${next}`); };
  const delBlob = (c, b) => act("/azure/storage/blobs/delete", { container: c.name, name: b.name }, "Blob deleted");
  const toggleImmut = (c) => act("/azure/storage/immutability", { container: c.name, retention_days: c.immutability?.retention_days ? 0 : 180, legal_hold: false }, c.immutability?.retention_days ? "Retention removed" : "180-day retention set");
  const toggleNet = () => act("/azure/storage/network", { default_action: sel.properties.network?.default_action === "Deny" ? "Allow" : "Deny", ip_rules: [] }, "Network rules updated");
  const genSas = async () => { const r = await run(() => api.post("/azure/storage/sas", { sa_id: sel.id, permissions: sasPerms, expiry_hours: 1 }), {}); if (r.ok && r.data) setSasUrl(r.data.url); };

  const create = async () => {
    const tags = {};
    tagRows.forEach(({ key, value }) => { const k = (key || "").trim(); if (k) tags[k] = value; });
    setBusy(true);
    const r = await run(() => api.post("/azure/storage-accounts", { ...form, tags }), { success: "Storage account created" });
    setBusy(false);
    if (r.ok) {
      setOpen(false);
      setForm({ name: "", resource_group: "", location: "eastus", sku: "Standard_LRS" });
      setTagRows([{ key: "", value: "" }]);
      refresh();
    }
  };

  return (
    <div data-testid="az-storage">
      <div className="flex items-center gap-2 border-b border-border pb-3 mb-3">
        <HardDrive className="h-5 w-5 text-[#0078d4]" /><h2 className="font-semibold">Storage accounts</h2>
        {!sel && (
          <Button size="sm" className="ml-auto gap-1.5 bg-[#0078d4] hover:bg-[#106ebe] text-white" onClick={() => setOpen(true)} data-testid="az-storage-create-btn">
            <Plus className="h-4 w-4" /> Create
          </Button>
        )}
      </div>
      {!sel ? (
        <div className="border border-border rounded-md overflow-hidden">
          <Table>
            <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Kind</TableHead><TableHead>Redundancy</TableHead><TableHead>Tags</TableHead><TableHead>Resource group</TableHead></TableRow></TableHeader>
            <TableBody>
              {accts.map((a) => (
                <TableRow key={a.id} className="cursor-pointer" data-testid={`az-storage-row-${a.name}`} onClick={() => setSelId(a.id)}>
                  <TableCell className="font-medium text-[#0078d4]">{a.name}</TableCell>
                  <TableCell>{a.properties.kind}</TableCell>
                  <TableCell>{a.properties.sku}</TableCell>
                  <TableCell className="text-xs">{Object.keys(a.tags || {}).length ? Object.entries(a.tags).map(([k, v]) => `${k}=${v}`).join(", ") : "—"}</TableCell>
                  <TableCell>{a.resource_group}</TableCell>
                </TableRow>
              ))}
              {accts.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-6">No storage accounts yet. Click <strong>Create</strong> to add one.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div>
          <button className="text-sm text-muted-foreground mb-3" onClick={() => setSelId(null)}>← Storage accounts</button>
          <div className="font-semibold mb-2 flex items-center gap-2"><HardDrive className="h-4 w-4 text-[#0078d4]" /> {sel.name}</div>
          <div className="text-xs text-muted-foreground mb-3">Redundancy: {sel.properties.sku} · RG: {sel.resource_group} · Tags: {Object.keys(sel.tags || {}).length ? Object.entries(sel.tags).map(([k, v]) => `${k}=${v}`).join(", ") : "none"}</div>

          {/* Networking + SAS toolbar */}
          <div className="flex flex-wrap items-center gap-2 mb-3 p-2 rounded-md border border-border bg-card" data-testid="az-storage-toolbar">
            <span className="text-xs text-muted-foreground">Public network access: <strong>{sel.properties.public_network_access}</strong></span>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={toggleNet} data-testid="az-storage-net-toggle">{sel.properties.network?.default_action === "Deny" ? "Allow public access" : "Deny public access"}</Button>
            <span className="mx-1 text-muted-foreground">|</span>
            <Input value={sasPerms} onChange={(e) => setSasPerms(e.target.value)} className="h-7 w-28 text-xs" placeholder="perms e.g. rl" data-testid="az-storage-sas-perms" />
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={genSas} data-testid="az-storage-sas-btn">Generate SAS</Button>
          </div>
          {sasUrl && <div className="text-[11px] font-mono break-all bg-muted rounded p-2 mb-3" data-testid="az-storage-sas-url">{sasUrl}</div>}

          {/* New container + upload */}
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <Input value={cname} onChange={(e) => setCname(e.target.value)} className="h-8 w-44 text-xs" placeholder="new container name" data-testid="az-storage-container-name" />
            <Button size="sm" className="h-8 text-xs bg-[#0078d4] text-white" onClick={addContainer} data-testid="az-storage-container-add">+ Container</Button>
            <span className="mx-1 text-muted-foreground">|</span>
            <Select value={bcont} onValueChange={setBcont}>
              <SelectTrigger className="h-8 w-40 text-xs" data-testid="az-storage-upload-container"><SelectValue placeholder="container" /></SelectTrigger>
              <SelectContent>{(sel.properties.containers || []).map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
            <Input value={bname} onChange={(e) => setBname(e.target.value)} className="h-8 w-44 text-xs" placeholder="blob file name" data-testid="az-storage-blob-name" />
            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={uploadBlob} data-testid="az-storage-upload-btn">Upload blob</Button>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {(sel.properties.containers || []).map((c) => (
              <div key={c.name} className="border border-border rounded-md p-3" data-testid={`az-container-${c.name}`}>
                <div className="flex items-center gap-2 text-sm font-medium">
                  <FolderOpen className="h-4 w-4 text-[#0078d4]" /> {c.name} <span className="text-xs text-muted-foreground">({c.access})</span>
                  {c.immutability?.retention_days > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-600">Immutable {c.immutability.retention_days}d</span>}
                  <Button size="sm" variant="ghost" className="ml-auto h-6 text-[11px]" onClick={() => toggleImmut(c)} data-testid={`az-container-immut-${c.name}`}>{c.immutability?.retention_days ? "Remove retention" : "Set retention"}</Button>
                </div>
                <div className="mt-2 space-y-1">
                  {c.blobs.map((b) => (
                    <div key={b.name} className="flex items-center gap-2 text-xs text-muted-foreground" data-testid={`az-blob-${c.name}-${b.name}`}>
                      <FileText className="h-3.5 w-3.5" /> {b.name}
                      <button className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-secondary text-secondary-foreground" onClick={() => cycleTier(c, b)} title="Cycle access tier">{b.tier}</button>
                      <span className="ml-auto">{(b.size_kb / 1024).toFixed(1)} MB</span>
                      <button className="text-red-500" onClick={() => delBlob(c, b)} data-testid={`az-blob-del-${c.name}-${b.name}`}><X className="h-3.5 w-3.5" /></button>
                    </div>
                  ))}
                  {c.blobs.length === 0 && <div className="text-xs text-muted-foreground">Empty container.</div>}
                </div>
              </div>
            ))}
            {(sel.properties.containers || []).length === 0 && <div className="text-xs text-muted-foreground">No containers yet — create one above.</div>}
          </div>

          <div className="mt-4">
            <div className="flex items-center gap-2 mb-1">
              <div className="text-sm font-medium">File shares</div>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => act("/azure/storage/file-shares", { name: `share${(sel.properties.file_shares || []).length + 1}`, quota_gb: 100 }, "File share created")} data-testid="az-storage-share-add">+ File share</Button>
            </div>
            {(sel.properties.file_shares || []).map((f) => (
              <div key={f.name} className="flex items-center gap-2 text-xs text-muted-foreground" data-testid={`az-share-${f.name}`}><Globe className="h-3.5 w-3.5" /> {f.name} — quota {f.quota_gb} GB</div>
            ))}
            {(sel.properties.file_shares || []).length === 0 && <div className="text-xs text-muted-foreground">No file shares.</div>}
          </div>
          <div className="mt-4 pt-3 border-t border-border" data-testid="az-storage-tags-editor">
            <div className="flex items-center gap-2 mb-1">
              <div className="text-sm font-medium inline-flex items-center gap-1.5"><Tag className="h-3.5 w-3.5" /> Tags</div>
              <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs" onClick={() => setDetTagRows((r) => [...r, { key: "", value: "" }])} data-testid="az-storage-detail-tag-add"><Plus className="h-3.5 w-3.5" /> Add tag</Button>
            </div>
            <p className="text-[11px] text-muted-foreground mb-2">Tag names and values are case-sensitive (e.g. key <span className="font-mono">CostCenter</span>).</p>
            <div className="space-y-2">
              {detTagRows.map((row, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`az-storage-detail-tag-row-${i}`}>
                  <Input placeholder="Name" value={row.key} onChange={(e) => setDetTagRows((r) => r.map((x, idx) => idx === i ? { ...x, key: e.target.value } : x))} className="h-8 text-xs" data-testid={`az-storage-detail-tag-key-${i}`} />
                  <Input placeholder="Value" value={row.value} onChange={(e) => setDetTagRows((r) => r.map((x, idx) => idx === i ? { ...x, value: e.target.value } : x))} className="h-8 text-xs" data-testid={`az-storage-detail-tag-value-${i}`} />
                  <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={() => setDetTagRows((r) => r.filter((_, idx) => idx !== i))} data-testid={`az-storage-detail-tag-remove-${i}`}><X className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
            <Button size="sm" className="mt-3 gap-2 bg-[#0078d4] hover:bg-[#106ebe] text-white" onClick={saveDetailTags} data-testid="az-storage-detail-tag-save"><Tag className="h-4 w-4" /> Apply tags</Button>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="az-storage-create-dialog">
          <DialogHeader><DialogTitle>Create a storage account</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Storage account name</Label><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="hmgstore01" data-testid="az-storage-name" /><p className="text-[11px] text-muted-foreground mt-1">3–24 chars, lowercase letters and numbers only.</p></div>
            <div><Label>Resource group</Label>
              <Select value={form.resource_group} onValueChange={(v) => setForm((f) => ({ ...f, resource_group: v }))}>
                <SelectTrigger data-testid="az-storage-rg"><SelectValue placeholder="Select a resource group" /></SelectTrigger>
                <SelectContent>{rgs.map((g) => <SelectItem key={g.name} value={g.name}>{g.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Region</Label>
                <Select value={form.location} onValueChange={(v) => setForm((f) => ({ ...f, location: v }))}>
                  <SelectTrigger data-testid="az-storage-region"><SelectValue /></SelectTrigger>
                  <SelectContent>{REGIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Redundancy</Label>
                <Select value={form.sku} onValueChange={(v) => setForm((f) => ({ ...f, sku: v }))}>
                  <SelectTrigger data-testid="az-storage-sku"><SelectValue /></SelectTrigger>
                  <SelectContent>{SKUS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="inline-flex items-center gap-1.5"><Tag className="h-3.5 w-3.5" /> Tags</Label>
                <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs" onClick={() => setTagRows((r) => [...r, { key: "", value: "" }])} data-testid="az-storage-tag-add"><Plus className="h-3.5 w-3.5" /> Add tag</Button>
              </div>
              <div className="space-y-2">
                {tagRows.map((row, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input placeholder="Name" value={row.key} onChange={(e) => setTagRows((r) => r.map((x, idx) => idx === i ? { ...x, key: e.target.value } : x))} className="h-8 text-xs" data-testid={`az-storage-tag-key-${i}`} />
                    <span className="text-muted-foreground text-xs">=</span>
                    <Input placeholder="Value" value={row.value} onChange={(e) => setTagRows((r) => r.map((x, idx) => idx === i ? { ...x, value: e.target.value } : x))} className="h-8 text-xs" data-testid={`az-storage-tag-value-${i}`} />
                    <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={() => setTagRows((r) => r.filter((_, idx) => idx !== i))}><X className="h-4 w-4" /></Button>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground mt-1.5">If a resource group has a "Require a tag" policy, creating without that tag is blocked (Review + create fails).</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-[#0078d4] hover:bg-[#106ebe] text-white" disabled={busy || !form.name || !form.resource_group} onClick={create} data-testid="az-storage-submit">
              {busy ? "Creating…" : "Review + create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
